import React, { Fragment } from "react";
import { connect } from "react-redux";
import { Dispatch, AnyAction } from 'redux';
import { IAppState, NavigationTarget, WebchatOptions } from "../../Redux/reduxState";
import { VoiceCustomizationEntry } from "../constants/voiceCustomizationEntries";
import { genericSingleAction, actionTypes } from "../../Redux/actions";
import { VoiceCategory } from "../../utilities/types";
import { CustomizationEntrySelector } from "./customizationEntrySelector";
import { CustomizationEntrySubCategory } from "./customizationEntrySubCategory";
import { mergeStyles } from "@fluentui/react";
import { ContainerName } from "../../Redux/reducers/AppReducer";

const entryViewerContainer = mergeStyles(
    {
        display: 'block',
        paddingTop: '25px',
        paddingBottom: '25px',
        '> div:last-of-type': {
            borderBottom: '1px solid var(--druid-color-border) !important',
        },
    }
);

const defaultEntriesContainer = mergeStyles(
    {
        marginBottom: '10px',
    }
);

interface ISubCategoryObject {
    [key: string]: VoiceCustomizationEntry[],
}

interface StateProps {
    activeVoiceCategory: VoiceCategory;
    voiceCustomizationEntries: VoiceCustomizationEntry[];
    webchatOptions: WebchatOptions;
    navigationTarget?: NavigationTarget;
}

interface DispatchProps {
    updateStyleElement: (styleElementName: string, containerName: ContainerName, value: any) => void;
}

interface Props {
    updateRootStateVariable: (stateVariableName: string, value: any) => void,
}

type PropsType = StateProps & DispatchProps & Props;

export class VoiceConfigurationEntryViewer extends React.Component<PropsType> {
    constructor(props: PropsType) {
        super(props);
    }

    createSubCategoriesObject = (): ISubCategoryObject => {
        const { voiceCustomizationEntries, activeVoiceCategory } = this.props
        const subCategoryObject: ISubCategoryObject = {};

        voiceCustomizationEntries.forEach((entry: VoiceCustomizationEntry) => {
            if (entry.category === activeVoiceCategory) {
                if (entry.subCategory) {
                    if (subCategoryObject[entry.subCategory]) {
                        subCategoryObject[entry.subCategory].push(entry);
                    } else {
                        subCategoryObject[entry.subCategory] = [entry];
                    }
                } else {
                    if (subCategoryObject.noSubCategory) {
                        subCategoryObject.noSubCategory.push(entry);
                    } else {
                        subCategoryObject.noSubCategory = [entry];
                    }
                }
            }
        });

        return subCategoryObject;
    }

    getStyleOptionValue = (key: any, containerName: ContainerName = "VoiceUI") => {
        const { webchatOptions } = this.props;
        const containerValue = containerName === "root" ? webchatOptions : containerName === "UI.headerOptions" ? webchatOptions["UI"]["headerOptions"] : webchatOptions[containerName];
        return this.prop(containerValue, key);
    };

    prop<T, K extends keyof T>(obj: T, key: K) {
        if (!obj) {
            return null;
        }
        return obj[key];
    }

    mapSubCategoriesObjectToReactElements = (subCategoryObject: ISubCategoryObject) => {
        const { webchatOptions, updateStyleElement } = this.props;
        return (
            <>
                {Object.keys(subCategoryObject).map((key, index) => {
                    if (key === "noSubCategory") {
                        return <div key={key} className={defaultEntriesContainer}>
                            {subCategoryObject[key].map((entry: VoiceCustomizationEntry) => (
                                <CustomizationEntrySelector
                                    key={entry.id}
                                    entry={entry}
                                    value={this.getStyleOptionValue(entry.id, entry.containerName)}
                                    onChange={updateStyleElement}
                                    getStyleOptionValue={(key, containerName) => this.getStyleOptionValue(key, containerName || entry.containerName)}
                                    highlighted={this.props.navigationTarget?.entryId === entry.id}
                                />
                            ))}
                        </div>
                    } else {
                        return <CustomizationEntrySubCategory
                            key={key}
                            entries={subCategoryObject[key]}
                            subCategory={key}
                            webchatOptions={webchatOptions}
                            updateStyleElement={updateStyleElement}
                            getStyleOptionValue={this.getStyleOptionValue}
                            revealRequested={this.props.navigationTarget?.subCategory === key}
                            revealEntryId={this.props.navigationTarget?.entryId}
                            highlightEntryId={this.props.navigationTarget?.entryId}
                        />
                    }
                })}
            </>
        );
    };

    render() {
        const { activeVoiceCategory } = this.props;
        const subCategoriesObject = this.createSubCategoriesObject();

        return (
            <Fragment>
                <h5>{activeVoiceCategory}</h5>
                <div className={entryViewerContainer}>
                    {this.mapSubCategoriesObjectToReactElements(subCategoriesObject)}
                </div>
            </Fragment>
        );
    }
}

const mapStateToProps = (state: IAppState, ownProps: Props): StateProps => ({
    activeVoiceCategory: state.activeVoiceCategory,
    voiceCustomizationEntries: state.voiceCustomizationEntries,
    webchatOptions: state.webchatOptions,
    navigationTarget: state.navigationTarget,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): DispatchProps => ({
    updateStyleElement: (styleElementName: string, value: any, containerName?: ContainerName) => {
        dispatch(genericSingleAction<any>(actionTypes.UPDATE_STYLE_ELEMENT, { styleElementName: styleElementName, value: value, containerName: containerName }));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(VoiceConfigurationEntryViewer);
